import { Controller } from "@hotwired/stimulus"
import { toISODateString } from "../../utils/date-format.util"
import { formatDateWith } from "../../utils/server-format.util"

// Connects to data-controller="document-generator--form"
export default class extends Controller {
  static targets = [
    "endDate",
    "endDateRow",
    "generationsCount",
    "generationsCountRow",
    "condition",
    "endOfMonth",
    "startDate",
    "frequency",
    "deliverByEmail",
    "emailRow",
    "emailTimingRow",
    "emailTiming",
    "emailHourRow",
    "emailHour",
    "fixedVariableSymbol",
    "variableSymbolRow",
    "emailTimingHint",
    "emailTimingHintRow",
    "deliveryEndOfPeriod",
    "deliveryDateOffset",
    "nextIssueDate",
    "recipient",
    "recipientHint"
  ]

  static values = {
    emailTimingHints: { type: Object, default: {} },
    recipientHints: { type: Object, default: {} },
    // What the server put in the recipient field as the contact's address. The
    // field is an OVERRIDE — blank means "use the customer's e-mail at send
    // time" — so a value that is merely the contact's may be replaced when the
    // customer changes, while anything the user typed must survive.
    prefilledEmail: { type: String, default: "" },
    // date_format_data(:short) — the pattern and names the server printed the
    // persisted next issue date with, so the live preview reads the same.
    dateFormat: { type: Object, default: {} }
  }

  connect() {
    this.endConditionChanged()
    this.toggleEndOfMonthByFrequency()
    this.updateStartDateFromEndOfMonth()
    this.toggleEmailFields()
    this.toggleEmailHourField()
    this.#updateNextIssueDate()

    this._selectedContactListener = this.#contactSelected.bind(this)
    document.addEventListener("selectedContact", this._selectedContactListener)
  }

  disconnect() {
    document.removeEventListener("selectedContact", this._selectedContactListener)
  }

  toggleEndOfMonthByFrequency() {
    const frequency = this.frequencyTarget.value
    const isLessThanMonthly = ["daily", "weekly", "biweekly", "every_5_minutes"].includes(frequency)
    this.endOfMonthTarget.disabled = isLessThanMonthly

    // Toggle disabled class on toggle-switch wrapper
    const toggleSwitch = this.endOfMonthTarget.closest(".toggle-switch")
    if (toggleSwitch) {
      toggleSwitch.classList.toggle("disabled", isLessThanMonthly)
    }

    // If disabling and it was checked, uncheck it
    if (isLessThanMonthly && this.endOfMonthTarget.checked) {
      this.endOfMonthTarget.checked = false
      this.updateStartDateFromEndOfMonth()
    }
    this.#updateNextIssueDate()
  }
  startDateChanged() {
    // #parseDateValue anchors at local noon. Parsing the raw value instead made
    // `new Date("2026-08-31")` midnight UTC while getDate() below reads local —
    // so west of UTC a month-end date reported as the 30th and silently switched
    // the end_of_month toggle off, dropping the user into the month-end drift.
    const date = this.#parseDateValue(this.startDateTarget.value)
    if (!date) return

    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0, 12).getDate()
    const isMonthEnd = date.getDate() === lastDay

    this.endOfMonthTarget.checked = isMonthEnd
    this.endOfMonthTarget.value = isMonthEnd
    this.#updateNextIssueDate()
  }

  endOfMonthChanged() {
    this.updateStartDateFromEndOfMonth()
    this.toggleEndOfMonthByFrequency()
    this.#updateNextIssueDate()
  }

  updateStartDateFromEndOfMonth() {
    if (this.endOfMonthTarget.checked) {
      // Snap to the end of the month the field ALREADY names — never to today's.
      // connect() runs this on every page load and `start_date` is a permitted
      // param, so inventing a date here rewrote the stored value on any save.
      // Production generator 11 stored 2026-08-31 and the form showed 09/30/2026.
      // A genuinely new record has an empty field; only then does today apply.
      const anchor = this.#parseDateValue(this.startDateTarget.value) || new Date()
      const lastDay = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0, 12)
      this.endOfMonthTarget.value = true
      this.startDateTarget.value = toISODateString(lastDay)
      this.startDateTarget.readOnly = true
      this.startDateTarget.classList.add("bg-light", "text-muted")
    } else {
      this.endOfMonthTarget.value = false
      this.startDateTarget.readOnly = false
      this.startDateTarget.classList.remove("bg-light", "text-muted")
    }
  }

  endConditionChanged() {
    const value = this.conditionTarget.value

    if (value === "max_generations") {
      this.generationsCountRowTarget.classList.remove("d-none")
      this.endDateRowTarget.classList.add("d-none")
    } else if (value === "end_date") {
      this.endDateRowTarget.classList.remove("d-none")
      this.generationsCountRowTarget.classList.add("d-none")
    } else {
      this.endDateRowTarget.classList.add("d-none")
      this.generationsCountRowTarget.classList.add("d-none")
    }
  }

  deliverByEmailChanged() {
    this.toggleEmailFields()
  }

  emailTimingChanged() {
    this.toggleEmailHourField()
    this.updateEmailTimingHint()
  }

  // The hour belongs to the "custom" timing alone; for every other one the
  // scheduler ignores it, so showing it would offer a setting with no effect.
  toggleEmailHourField() {
    if (!this.hasEmailTimingTarget || !this.hasEmailHourRowTarget) return

    const isCustom = this.emailTimingTarget.value === "custom"
    const emailOn = !this.hasDeliverByEmailTarget || this.deliverByEmailTarget.checked
    this.emailHourRowTarget.classList.toggle("d-none", !(isCustom && emailOn))
  }

  // The hint is the label's tooltip, not a paragraph under the field, so this
  // rewrites the tooltip's title value — tooltip_controller#titleValueChanged
  // pushes it into the live Bootstrap instance — and the accessible name with
  // it, since the two carry the same sentence.
  updateEmailTimingHint() {
    if (!this.hasEmailTimingHintTarget || !this.hasEmailTimingTarget) return

    const hint = this.emailTimingHintsValue[this.emailTimingTarget.value] || ""
    this.emailTimingHintTarget.setAttribute("data-tooltip-title-value", hint)
    this.emailTimingHintTarget.setAttribute("aria-label", hint)
  }

  toggleEmailFields() {
    if (!this.hasDeliverByEmailTarget) return

    const enabled = this.deliverByEmailTarget.checked
    if (this.hasEmailRowTarget) {
      this.emailRowTarget.classList.toggle("d-none", !enabled)
    }
    if (this.hasEmailTimingRowTarget) {
      this.emailTimingRowTarget.classList.toggle("d-none", !enabled)
    }
    if (this.hasEmailTimingHintRowTarget) {
      this.emailTimingHintRowTarget.classList.toggle("d-none", !enabled)
    }
    this.toggleEmailHourField()
  }

  // The recipient field is server-rendered from the customer chosen at page
  // load, and on a new template there is none yet. Picking one in the document
  // form has to reach the sidebar, or the field stays empty while the panel
  // claims the address comes from the contact.
  #contactSelected(event) {
    if (!this.hasRecipientTarget) return

    const email = (event.detail?.email || "").trim()
    const name = event.detail?.text || ""

    const field = this.recipientTarget
    // Trim BOTH sides: <input type="email"> strips surrounding whitespace from
    // its value, so an address stored with any would never compare equal and
    // the field would silently stop following its contact.
    const isOurs = field.value.trim() === "" || field.value.trim() === this.prefilledEmailValue.trim()
    if (isOurs) {
      field.value = email
      this.prefilledEmailValue = email
    }

    this.#updateRecipientHint(name, email, isOurs)
  }

  #updateRecipientHint(name, email, isOurs) {
    if (!this.hasRecipientHintTarget) return

    const hints = this.recipientHintsValue
    // Three states, and the kept-override one is why this takes isOurs: saying
    // "Z kontaktu Novák" under an address the user typed themselves describes a
    // send that is not going to happen.
    let template = hints.override
    if (isOurs) template = email ? hints.fromContact : hints.missing
    if (!template) return

    // The function form: a contact named "Kaviareň $&" would otherwise have its
    // own name spliced in by replace()'s $-patterns.
    this.recipientHintTarget.textContent = template.replace("%{name}", () => name)
  }

  deliveryEndOfPeriodChanged() {
    if (!this.hasDeliveryEndOfPeriodTarget || !this.hasDeliveryDateOffsetTarget) return

    const endOfPeriod = this.deliveryEndOfPeriodTarget.checked
    this.deliveryDateOffsetTarget.disabled = endOfPeriod
    if (endOfPeriod) {
      this.deliveryDateOffsetTarget.value = ""
    }
  }

  fixedVariableSymbolChanged() {
    if (!this.hasFixedVariableSymbolTarget || !this.hasVariableSymbolRowTarget) return

    const enabled = this.fixedVariableSymbolTarget.checked
    this.variableSymbolRowTarget.style.display = enabled ? "" : "none"
  }

  // `new Date("2026-08-31")` is midnight UTC, but every getter we use is local —
  // west of UTC that reads back as the 30th. Anchoring at local noon keeps the
  // calendar day intact in every zone, which is what #updateNextIssueDate
  // already does below.
  #parseDateValue(value) {
    if (!value) return null

    const parsed = new Date(`${value}T12:00:00`)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  #updateNextIssueDate() {
    if (!this.hasNextIssueDateTarget) return

    // Through #parseDateValue like every other read of this field — it applies
    // the same local-noon anchor this method used to inline, and returns null on
    // an unparseable value instead of feeding an Invalid Date into the preview.
    // One parse rule in the file, not two.
    const base = this.#parseDateValue(this.startDateTarget.value)
    const frequency = this.frequencyTarget.value

    if (!base || !frequency) {
      this.nextIssueDateTarget.textContent = "—"
      return
    }

    const nextDate = this.#computeNextDate(base, frequency, this.endOfMonthTarget.checked)
    // Exactly l(date, format: :short), which rendered this element server-side.
    this.nextIssueDateTarget.textContent = nextDate ? formatDateWith(nextDate, this.dateFormatValue) : "—"
  }

  #computeNextDate(base, frequency, endOfMonth) {
    let shifted

    switch (frequency) {
      case "daily":
        shifted = this.#addDays(base, 1)
        break
      case "weekly":
        shifted = this.#addDays(base, 7)
        break
      case "biweekly":
        shifted = this.#addDays(base, 14)
        break
      case "monthly":
        shifted = this.#addMonths(base, 1)
        break
      case "quarterly":
        shifted = this.#addMonths(base, 3)
        break
      case "semiannually":
        shifted = this.#addMonths(base, 6)
        break
      case "yearly":
        shifted = this.#addMonths(base, 12)
        break
      default:
        return null
    }

    if (endOfMonth) {
      return new Date(shifted.getFullYear(), shifted.getMonth() + 1, 0, 12)
    }
    return shifted
  }

  // Ruby's `+ 1.month` CLAMPS (Jan 31 -> Feb 28); JS `setMonth(+1)` OVERFLOWS
  // (Jan 31 -> Mar 3). DocumentGenerator#next_issue_date_after_shift uses the
  // Ruby form, so an unclamped preview disagreed with the backend in Jan, Mar,
  // May, Aug and Oct — and with end_of_month on it then snapped to the WRONG
  // month's end. `+ 1.year` clamps a leap day the same way, hence yearly = 12
  // months rather than setFullYear.
  #addMonths(date, months) {
    const day = date.getDate()
    const firstOfTarget = new Date(date.getFullYear(), date.getMonth() + months, 1, 12)
    const daysInTarget = new Date(firstOfTarget.getFullYear(), firstOfTarget.getMonth() + 1, 0, 12).getDate()

    firstOfTarget.setDate(Math.min(day, daysInTarget))
    return firstOfTarget
  }

  // Day arithmetic is exact on both sides; rebuilding at local noon keeps a DST
  // transition from shifting the calendar day.
  #addDays(date, days) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days, 12)
  }
}
